// filepath: apps/web/src/features/admin/templates/export-import/deriveTemplateSource.ts
//
// FR-UI-ADM-13 AC-3 (Q2) — client-side derivation of the
// `source` field for the per-row "imported" / "native" badge.
//
// SCOPE DECISION (Q2, embedded in 01-requirement-validation.md
// `architectural_placement.scope_decisions[].id="Q2-source-field"`):
//   The `TemplateRegistryRow` wire type is NOT extended with a
//   server-side `source` field. The frontend derives the source
//   client-side via this helper.
//
// DERIVATION RULE:
//   `source === "imported"` IF AND ONLY IF
//     row.published_by.startsWith("system:platform-admin") AND
//     row.publisher_kind === "platform"
//   OTHERWISE `source === "native"`.
//
// RATIONALE:
//   The server-side `published_by` field is `"system:platform-admin"`
//   for BOTH imports AND vet-via-system operations; naive
//   `startsWith("system:platform-admin")` alone would mis-classify
//   system-vetted templates as imported. The conjunctive
//   `publisher_kind === "platform"` filter eliminates the
//   ambiguity — platform-vetted (non-imported) templates have
//   `publisher_kind === "tenant"` (the original author was a
//   tenant) and only platform-imported templates have
//   `publisher_kind === "platform"` plus the
//   `system:platform-admin` `published_by`.
//
// CONSUMERS:
//   - `TemplateRegistryTable.tsx` renders the badge text-only
//     next to the `version` column.
//   - `TemplateRegistryPage.tsx` does NOT consume the helper
//     directly (the table is the rendering surface).
//
// PURE FUNCTION — no side effects, no TanStack Query, no
// localStorage. The same derivation runs identically in the
// test harness.

import type { TemplateRegistryRow } from "../registry/types";
import type { SourceKind } from "./types";

/**
 * deriveTemplateSource — pure helper that maps a
 * `TemplateRegistryRow` to its `SourceKind`.
 *
 * @param row  The registry row to classify.
 * @returns    `"imported"` if the row was imported by a platform
 *             admin (and is platform-published); `"native"`
 *             otherwise (i.e. tenant-authored, system-vetted, or
 *             platform-published by a non-system publisher).
 */
export function deriveTemplateSource(row: TemplateRegistryRow): SourceKind {
  if (
    typeof row.published_by === "string" &&
    row.published_by.startsWith("system:platform-admin") &&
    row.publisher_kind === "platform"
  ) {
    return "imported";
  }
  return "native";
}
